
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { User, Lead, CustomFieldDefinition } from '../types';
import { api } from '../services/apiService';
import SelectField from './SelectField';

const DEFAULT_REVENUE_BRACKETS = ['<5K', '5-20K', '20-50K', '50-100K', '>100K'];

interface AddLeadModalProps {
  currentUser: User;
  revenueBrackets?: string[];
  customFieldDefinitions?: CustomFieldDefinition[];
  onClose: () => void;
  onSubmit: (leadData: Omit<Lead, 'id' | 'createdAt'>) => Promise<void> | void;
}

const AddLeadModal: React.FC<AddLeadModalProps> = ({ currentUser, revenueBrackets: propBrackets, customFieldDefinitions = [], onClose, onSubmit }) => {
  const effectiveBrackets = (propBrackets && propBrackets.length > 0) ? propBrackets : DEFAULT_REVENUE_BRACKETS;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sectors, setSectors] = useState<string[]>([]);
  const [showCustomRevenue, setShowCustomRevenue] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const [formData, setFormData] = useState({
    clientCompanyName: '',
    tag: '',
    sectorActivity: '',
    name: '',
    email: '',
    phone: '',
    phoneLandline: '',
    status: 'new' as Lead['status'],
    additionalInfo: '',
    potentialRevenue: '',
    source: ''
  });

  useEffect(() => {
    api.getActivitySectors().then(list => setSectors(list || [])).catch(() => setSectors([]));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation des champs obligatoires
    if (!formData.clientCompanyName.trim()) {
      alert("Le client (entreprise) est obligatoire.");
      return;
    }

    if (!formData.name.trim()) {
      alert("Le nom du prospect est obligatoire.");
      return;
    }

    if (!formData.email.trim()) {
      alert("L'email du prospect est obligatoire.");
      return;
    }

    // Validation email basique
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert("Veuillez saisir un email valide.");
      return;
    }

    if (isSubmitting) return; // Éviter les doubles soumissions
    setIsSubmitting(true);
    
    // Validation champs custom requis
    for (const field of customFieldDefinitions.filter(f => f.required && f.name)) {
      const val = customFieldValues[field.id];
      if (val === undefined || val === '' || val === false) {
        alert(`Le champ "${field.name}" est obligatoire.`);
        return;
      }
    }

    const leadData = {
      ...formData,
      companyId: currentUser.companyId,
      assignedTo: currentUser.id,
      isFavorite: false,
      customFields: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined
    };

    try {
      // Attendre que la création soit terminée
      await onSubmit(leadData);
      
      // Fermer la modal après succès
      onClose();
      
    } catch (error: any) {
      const msg = error?.message || '';
      if (msg.startsWith('DUPLICATE:')) {
        const dupInfo = msg.replace('DUPLICATE:', '');
        const forceCreate = confirm(`Un prospect similaire existe deja :\n${dupInfo}\n\nVoulez-vous quand meme creer ce prospect ?`);
        if (forceCreate) {
          try {
            await onSubmit({ ...leadData, forceDuplicate: true } as any);
            onClose();
          } catch {
            alert("Erreur lors de la creation du prospect.");
          }
        }
        setIsSubmitting(false);
      } else {
        console.error("Erreur lors de la creation du prospect:", error);
        alert("Une erreur est survenue lors de la creation du prospect. Veuillez reessayer.");
        setIsSubmitting(false);
      }
    }
  };

  const modalContent = (
    <div className="orion-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center p-6 backdrop-blur-md animate-in fade-in duration-300">
      <div 
        className="absolute inset-0" 
        onClick={!isSubmitting ? onClose : undefined}
      ></div>
      
      <div className="orion-modal-panel relative w-full max-w-lg rounded-[3rem] shadow-2xl p-10 border animate-in zoom-in-95">
        <h3 className="text-2xl font-black mb-2 dark:text-white tracking-tight">Nouveau Prospect</h3>
        <p className="text-slate-500 dark:text-slate-400 mb-8 text-sm">Créez une nouvelle opportunité commerciale</p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
              Client (entreprise) <span className="text-red-500">*</span>
            </label>
            <input
              required
              type="text"
              value={formData.clientCompanyName}
              onChange={e => setFormData({...formData, clientCompanyName: e.target.value})}
              placeholder="ex: ACME Industries"
              className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
              Nom complet <span className="text-red-500">*</span>
            </label>
            <input 
              required
              type="text" 
              value={formData.name} 
              onChange={e => setFormData({...formData, name: e.target.value})} 
              placeholder="ex: Jean Dupont" 
              className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
              disabled={isSubmitting}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
                Tag
              </label>
              <input
                type="text"
                value={formData.tag}
                onChange={e => setFormData({ ...formData, tag: e.target.value })}
                placeholder="ex: Prioritaire"
                className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
                Secteur d'activite
              </label>
              <select
                title="Secteur d'activité"
                value={formData.sectorActivity}
                onChange={e => setFormData({ ...formData, sectorActivity: e.target.value })}
                className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                disabled={isSubmitting}
              >
                <option value="">Selectionner</option>
                {sectors.map(sector => (
                  <option key={sector} value={sector}>
                    {sector}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input 
                required
                type="email" 
                value={formData.email} 
                onChange={e => setFormData({...formData, email: e.target.value})} 
                placeholder="jean@client.com" 
                className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
                Téléphone mobile
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={e => setFormData({...formData, phone: e.target.value})}
                placeholder="+33 6 12 34 56 78"
                className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
                Téléphone fixe
              </label>
              <input
                type="tel"
                value={formData.phoneLandline}
                onChange={e => setFormData({...formData, phoneLandline: e.target.value})}
                placeholder="+33 1 23 45 67 89"
                className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
              Revenu potentiel
            </label>
            <div className="flex flex-wrap gap-2">
              {effectiveBrackets.map(bracket => (
                <button
                  key={bracket}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    setFormData({ ...formData, potentialRevenue: formData.potentialRevenue === bracket ? '' : bracket });
                    setShowCustomRevenue(false);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                    formData.potentialRevenue === bracket
                      ? 'orion-revenue-chip-active'
                      : 'orion-revenue-chip-muted'
                  }`}
                >
                  {bracket}
                </button>
              ))}
              {/* Chip "Autre montant" */}
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  setShowCustomRevenue(!showCustomRevenue);
                  if (!showCustomRevenue && effectiveBrackets.includes(formData.potentialRevenue)) {
                    setFormData({ ...formData, potentialRevenue: '' });
                  }
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                  showCustomRevenue || (!effectiveBrackets.includes(formData.potentialRevenue) && formData.potentialRevenue)
                    ? 'orion-revenue-chip-active'
                    : 'orion-revenue-chip-muted'
                }`}
              >
                {!effectiveBrackets.includes(formData.potentialRevenue) && formData.potentialRevenue
                  ? `✎ ${formData.potentialRevenue}`
                  : 'Autre montant…'}
              </button>
            </div>
            {showCustomRevenue && (
              <div className="mt-3 animate-in slide-in-from-top-1 duration-150">
                <input
                  type="text"
                  value={
                    !effectiveBrackets.includes(formData.potentialRevenue) ? formData.potentialRevenue : ''
                  }
                  onChange={e => setFormData({ ...formData, potentialRevenue: e.target.value })}
                  placeholder="ex: 75 000 €, Environ 200K…"
                  className="orion-modal-input w-full border rounded-2xl px-5 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                  disabled={isSubmitting}
                  autoFocus
                />
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
              Source
            </label>
            <SelectField
              value={formData.source}
              onChange={val => setFormData({...formData, source: val})}
              options={[
                { value: '', label: 'Non renseignee' },
                { value: 'website', label: 'Site web' },
                { value: 'phone', label: 'Telephone' },
                { value: 'email', label: 'Email' },
                { value: 'linkedin', label: 'LinkedIn' },
                { value: 'referral', label: 'Recommandation' },
                { value: 'salon', label: 'Salon / Evenement' },
                { value: 'other', label: 'Autre' },
              ]}
              className="w-full rounded-2xl px-5 py-4 text-sm font-bold"
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">
              Note initiale
            </label>
            <textarea 
              value={formData.additionalInfo} 
              onChange={e => setFormData({...formData, additionalInfo: e.target.value})} 
              placeholder="Informations complémentaires sur le prospect..."
              className="orion-modal-input w-full border rounded-2xl px-5 py-4 text-sm font-medium h-24 resize-none outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
              disabled={isSubmitting}
            />
          </div>
          
          {/* Champs personnalisés */}
          {customFieldDefinitions.filter(f => f.name).length > 0 && (
            <div className="border-t border-slate-200 dark:border-slate-700 pt-6 mt-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 block">Champs personnalisés</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {customFieldDefinitions.filter(f => f.name).map(field => (
                  <div key={field.id}>
                    <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400 mb-1 block">
                      {field.name}{field.required && <span className="text-rose-400 ml-0.5">*</span>}
                    </label>
                    {field.type === 'text' && (
                      <input type="text" title={field.name} value={customFieldValues[field.id] || ''} onChange={(e) => setCustomFieldValues(p => ({ ...p, [field.id]: e.target.value }))} placeholder={field.name} className="w-full orion-modal-input rounded-xl px-4 py-3 text-sm font-bold" />
                    )}
                    {field.type === 'number' && (
                      <input type="number" title={field.name} value={customFieldValues[field.id] || ''} onChange={(e) => setCustomFieldValues(p => ({ ...p, [field.id]: e.target.value }))} placeholder="0" className="w-full orion-modal-input rounded-xl px-4 py-3 text-sm font-bold" />
                    )}
                    {field.type === 'date' && (
                      <input type="date" title={field.name} value={customFieldValues[field.id] || ''} onChange={(e) => setCustomFieldValues(p => ({ ...p, [field.id]: e.target.value }))} className="w-full orion-modal-input rounded-xl px-4 py-3 text-sm font-bold" />
                    )}
                    {field.type === 'select' && (
                      <select title={field.name} value={customFieldValues[field.id] || ''} onChange={(e) => setCustomFieldValues(p => ({ ...p, [field.id]: e.target.value }))} className="w-full orion-modal-input rounded-xl px-4 py-3 text-sm font-bold">
                        <option value="">— Sélectionner —</option>
                        {(field.options || []).filter(Boolean).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    )}
                    {field.type === 'checkbox' && (
                      <label className="flex items-center gap-2 cursor-pointer mt-1">
                        <input type="checkbox" title={field.name} checked={!!customFieldValues[field.id]} onChange={(e) => setCustomFieldValues(p => ({ ...p, [field.id]: e.target.checked }))} className="w-4 h-4 rounded" />
                        <span className="text-sm text-slate-400">Oui</span>
                      </label>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting} 
              className="orion-modal-ghost-btn flex-1 py-4 text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting} 
              className="orion-modal-primary-btn flex-1 py-4 text-white rounded-2xl text-xs font-black shadow-xl uppercase tracking-widest transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Création...</span>
                </>
              ) : (
                'Créer le prospect'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AddLeadModal;
